import { useTranslation } from 'hooks/useTranslation';
import useUser from 'features/user/hooks/useUser';
import { useForm } from 'react-hook-form';
import { useStyles } from './styles';
import { UploadVerificationDocumentsInput } from 'features/auth/ts';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import React, { useMemo } from 'react';
import useAuth from 'features/auth/hooks/useAuth';
import { Typography } from '@material-ui/core';
import { ReactComponent as TwoSquares } from 'assets/images/two-semi-transparent-squares.svg';

const INITIAL_FORM_STATE: UploadVerificationDocumentsInput = {
  personal_id: null,
  personal_id_filename: '',
  trade_license: null,
  trade_license_filename: '',
};

interface UploadVerificationProps {
  isReUpload?: boolean;
}

export const UploadVerification: React.FC<UploadVerificationProps> = ({ isReUpload = false }) => {
  const { t } = useTranslation('auth');
  const { userFullName } = useUser();
  const { onUploadVerificationDocuments, error } = useAuth();

  const form = useForm<UploadVerificationDocumentsInput>({
    mode: 'onSubmit',
    defaultValues: INITIAL_FORM_STATE,
  });

  const { handleSubmit, register, watch, reset } = form;

  const trade_license = watch('trade_license');
  const personal_id = watch('personal_id');

  const onSubmit = () => {
    if (personal_id && trade_license) {
      onUploadVerificationDocuments(personal_id[0], trade_license[0]);
    } else {
      reset({ trade_license: '', trade_license_filename: '', personal_id: '', personal_id_filename: '' });
    }
  };

  const isSubmitDisabled = useMemo(() => !trade_license || !personal_id, [trade_license, personal_id]);

  const classes = useStyles({ isSubmitDisabled });

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>
        {isReUpload ? t('reUploadVerification.title') : t('uploadVerification.title')}
      </Typography>

      <div className={classes.wrapper}>
        <div className={classes.infoWrapper}>
          {isReUpload ? (
            <Typography className={classes.title}>{t('reUploadVerification.infoTitle')}</Typography>
          ) : (
            <>
              <Typography className={classes.name}>
                {t('uploadVerification.hi')} <br /> {userFullName}
              </Typography>
              <Typography className={classes.title}>{t('welcomeToRewardsBy')}</Typography>
            </>
          )}

          <Typography className={classes.subtitle}>{t('uploadVerification.subtitle1')}</Typography>
          <Typography className={classes.subtitle}>{t('uploadVerification.subtitle2')}</Typography>
        </div>

        <div>
          {error && (
            <div className={classes.errorMsg}>
              <Typography>{error}</Typography>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
            <TwoSquares className={classes.topLeftSquares} />
            <TwoSquares className={classes.topRightSquares} />
            <TwoSquares className={classes.bottomSquares} />
            <div>
              <label htmlFor={'trade_license_filename'} className={classes.label}>
                {t('tradeLicense')}
              </label>

              <div className={classes.inputWrapper}>
                <TextField
                  {...register('trade_license_filename')}
                  value={(trade_license as unknown as FileList)?.[0]?.name ?? ''}
                  placeholder={t('uploadFile')}
                  InputProps={{
                    classes: {
                      input: classes.input,
                      disabled: classes.disabled,
                      underline: classes.underline,
                    },
                    readOnly: true,
                  }}
                  className={classes.textField}
                />

                <Button variant="contained" component="label" className={classes.browseFileBtn}>
                  {t('browseFile')}
                  <input
                    {...register('trade_license')}
                    type="file"
                    hidden
                    required
                    accept="image/png, image/jpeg, application/pdf"
                  />
                </Button>
              </div>
            </div>

            <div className={classes.personalIdField}>
              <label htmlFor={'personal_id_filename'} className={classes.label}>
                {t('validPersonalId')}
              </label>

              <div className={classes.inputWrapper}>
                <TextField
                  {...register('personal_id_filename')}
                  value={(personal_id as unknown as FileList)?.[0]?.name ?? ''}
                  placeholder={t('uploadFile')}
                  InputProps={{
                    classes: {
                      input: classes.input,
                      disabled: classes.disabled,
                      underline: classes.underline,
                    },
                    readOnly: true,
                  }}
                  className={classes.textField}
                />

                <Button variant="contained" component="label" className={classes.browseFileBtn}>
                  {t('browseFile')}
                  <input
                    {...register('personal_id')}
                    type="file"
                    hidden
                    required
                    accept="image/png, image/jpeg, application/pdf"
                  />
                </Button>
              </div>
            </div>

            <button type="submit" onSubmit={onSubmit} className={classes.submitBtn} disabled={isSubmitDisabled}>
              {t('submit', { ns: 'common' })}
              <i className={classes.arrow} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
